import { tokens } from '@fluentui/react-components';

import { buildDashboardSegmentSummaries, getDashboardSegmentLabel } from './dashboardPresentation';
import { EDashboardSegmentKey } from '../types/dashboard.types';
import type {
  TDashboardCounters,
  TDashboardSegmentPageResponse,
  TDashboardSegmentPageView,
  TDashboardOverviewResponse,
  TDashboardViewCounters,
  TDashboardViewModel,
  TDashboardStatusChartDatum,
} from '../types/dashboard.types';

type TDashboardStatusChartConfig = Omit<TDashboardStatusChartDatum, 'value'>;

const STATUS_CHART_CONFIG: TDashboardStatusChartConfig[] = [
  {
    key: 'draft',
    label: getDashboardSegmentLabel(EDashboardSegmentKey.DRAFT),
    color: '#6B5B95',
  },
  { key: 'reviewActive', label: 'Periodic review enabled', color: '#0067B8' },
  {
    key: 'openApprovals',
    label: getDashboardSegmentLabel(EDashboardSegmentKey.OPEN_APPROVAL),
    color: '#A67824',
  },
  {
    key: 'notApproved',
    label: getDashboardSegmentLabel(EDashboardSegmentKey.NOT_APPROVED),
    color: tokens.colorNeutralForeground3,
  },
  {
    key: 'approved',
    label: getDashboardSegmentLabel(EDashboardSegmentKey.APPROVED),
    color: '#357457',
  },
];

export const buildDashboardCounters = (counters: TDashboardCounters): TDashboardViewCounters => ({
  overdueReviews: counters.periodicReviews.overdue,
  reviewsDueSoon: counters.periodicReviews.dueSoon,
  openApprovals: counters.openApprovals,
  reviewActive: counters.periodicReviews.active,
  draft: counters.draft,
  notApproved: counters.notApproved,
  approved: counters.approved,
});

export const buildDashboardSegmentPage = (response: TDashboardSegmentPageResponse): TDashboardSegmentPageView => ({
  key: response.segment.key,
  label: getDashboardSegmentLabel(response.segment.key),
  page: response.segment.page + 1,
  pageSize: response.segment.pageSize,
  totalCount: response.segment.totalCount,
  items: response.segment.items,
});

const buildStatusChart = (counters: TDashboardViewCounters): TDashboardStatusChartDatum[] =>
  STATUS_CHART_CONFIG.map((datum) => ({ ...datum, value: counters[datum.key] }));

export const buildDashboardViewModel = (response: TDashboardOverviewResponse): TDashboardViewModel => {
  const counters = buildDashboardCounters(response.counters);

  return {
    counters,
    rootOptions: response.rootOptions,
    segments: buildDashboardSegmentSummaries(counters),
    statusChart: buildStatusChart(counters),
  };
};
